import { Bell, KeyRound, Mail, Phone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { Field, FormError } from '@/components/ui/field';
import {
  useClientProfile,
  useNotificationPreferences,
  useSetNotificationPreference,
  useSetRecoveryEmail,
} from '@/hooks/client-portal';
import { ApiError } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  CLIENT_NOTIFICATION_TYPES,
  CLIENT_NOTIFICATION_TYPE_HINTS,
  CLIENT_NOTIFICATION_TYPE_LABELS,
  type ClientProfileView,
} from '@shared/schemas/client-portal';

/*
 * /portal/profile — ROADMAP.md §5.7.
 *
 * **Read-only, and it says why.** That is question 10's answer and the
 * prototype's own choice: an address that can change under a job already
 * scheduled is an address a cleaner drives to and finds nobody at. The line
 * about ringing the office is not an apology for a missing feature — it is the
 * feature.
 *
 * Two things are not read-only, and both are about the *account* rather than
 * the customer record: the password, and the recovery email — the other half of
 * P9, and the first screen in this project where somebody can set one for
 * themselves.
 */

export function ProfilePage() {
  const profile = useClientProfile();

  return (
    <ClientPage title="Profile" description="Your account, and what we send you.">
      {profile.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your profile"
            hint="Something went wrong on our side. Try again — and ring the office on (435) 238-3286 if it keeps happening."
            onRetry={() => {
              void profile.refetch();
            }}
          />
        </div>
      ) : profile.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading your profile</span>
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : profile.data ? (
        <div className="space-y-6">
          <YourDetails profile={profile.data} />
          <SignIn profile={profile.data} />
          <NotificationPreferences />
        </div>
      ) : null}
    </ClientPage>
  );
}

function YourDetails({ profile }: { profile: ClientProfileView }) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">Your details</h2>

      <dl className="mt-3 grid gap-4 sm:grid-cols-2">
        <Detail label="Account">{profile.displayName}</Detail>
        <Detail label="Contact">{profile.contactName}</Detail>
        <Detail label="Email" absent="Not on file" icon={Mail}>
          {profile.email}
        </Detail>
        <Detail label="Phone" absent="Not on file" icon={Phone}>
          {profile.phone}
        </Detail>
        <Detail label="Customer since" absent="Not recorded">
          {profile.customerSince ? formatDay(profile.customerSince) : null}
        </Detail>
      </dl>

      {/*
        Question 10, said in the prototype's own words. It is a deliberate
        choice, not a missing feature — so it explains itself rather than
        apologising.
      */}
      <p className="mt-4 rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2.5 text-meta text-ink-700">
        To change your name, phone number or email, ring the office on (435) 238-3286 and a team
        member will update your account. Your addresses are yours to change —{' '}
        <Link to="/portal/properties" className="text-bark-700 underline underline-offset-2">
          manage them here
        </Link>
        .
      </p>
    </section>
  );
}

function SignIn({ profile }: { profile: ClientProfileView }) {
  const toast = useToast();
  const save = useSetRecoveryEmail();
  const [recoveryEmail, setRecoveryEmail] = useState(profile.recoveryEmail ?? '');

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  // A username account has no email at all, so without a recovery address a
  // password reset has nowhere to go — P9, the state the prototype has live.
  const usesUsername = profile.loginEmail === null;
  const strandable = usesUsername && profile.recoveryEmail === null;

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(
      { recoveryEmail },
      {
        onSuccess: () => {
          toast.success('Saved. A password reset will go to that address.');
        },
      },
    );
  }

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <KeyRound aria-hidden="true" className="size-[18px] text-bark-700" />
        Signing in
      </h2>

      <dl className="mt-3 grid gap-4 sm:grid-cols-2">
        <Detail label="You sign in with">{profile.loginEmail ?? profile.username}</Detail>
        <div>
          <dt className="text-meta text-ink-500">Password</dt>
          <dd className="mt-0.5">
            <Link
              to="/change-password"
              className="text-body text-bark-700 underline underline-offset-2"
            >
              Change your password
            </Link>
          </dd>
        </div>
      </dl>

      <form onSubmit={submit} className="mt-4 space-y-3 border-t border-ink-300 pt-4">
        {strandable ? (
          <p className="rounded-sm border border-status-priority-text/30 bg-red-50 px-3 py-2.5 text-meta text-status-priority-text">
            You sign in with a username, so we have no email for you. Without one here, we cannot
            send you a password reset and you would have to ring us to get back in.
          </p>
        ) : null}

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Recovery email"
          name="recoveryEmail"
          type="email"
          value={recoveryEmail}
          error={fieldErrors.recoveryEmail}
          onChange={(event) => {
            setRecoveryEmail(event.target.value);
          }}
          required={usesUsername}
          hint={
            profile.recoveryEmail === null
              ? 'Not set — add one to recover your account if you lose access.'
              : 'Where a password reset would be sent.'
          }
        />

        <Button type="submit" disabled={save.isPending || recoveryEmail.trim() === ''}>
          {save.isPending ? 'Saving…' : 'Save recovery email'}
        </Button>
      </form>
    </section>
  );
}

/**
 * What we send, how we send it, and what we cannot send yet.
 *
 * **Email became a real choice on 2026-08-07** — owner decision that
 * notifications go out as email as well as appearing here. Until then every row
 * had one checkbox and a note underneath saying email was coming.
 *
 * **Explain, don't hide** — design-system.md §7, and the prototype's own push
 * message is the model: it says the capability is blocked, why, and what would
 * fix it, rather than removing the control. That note now covers phone alerts
 * alone.
 */
function NotificationPreferences() {
  const toast = useToast();
  const preferences = useNotificationPreferences();
  const save = useSetNotificationPreference();

  const disabled = new Set(preferences.data?.disabled ?? []);

  const onError = (cause: unknown) => {
    toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
  };

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <Bell aria-hidden="true" className="size-[18px] text-bark-700" />
        What we tell you about
      </h2>

      {preferences.error ? (
        <ErrorState
          title="We could not load these"
          onRetry={() => {
            void preferences.refetch();
          }}
        />
      ) : (
        <ul className="mt-3 divide-y divide-ink-300">
          {CLIENT_NOTIFICATION_TYPES.map((type) => (
            <li
              key={type}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
            >
              <div className="min-w-0">
                {/* "Request Declined" — one word for the state (P11, P18). */}
                <p className="text-body text-ink-900">{CLIENT_NOTIFICATION_TYPE_LABELS[type]}</p>
                <p className="mt-0.5 text-meta text-ink-500">
                  {CLIENT_NOTIFICATION_TYPE_HINTS[type]}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-4">
                {[
                  { channel: 'in_app' as const, label: 'In the portal' },
                  { channel: 'email' as const, label: 'By email' },
                ].map(({ channel, label }) => {
                  const key = `${type}:${channel}`;

                  return (
                    <label key={key} className="flex min-h-touch items-center gap-2">
                      <input
                        type="checkbox"
                        name={key}
                        // **Absence means enabled**, which is why this reads a
                        // set of what is switched *off* rather than one of what
                        // is on. Nothing seeds preference rows.
                        checked={!disabled.has(key)}
                        disabled={preferences.isPending || save.isPending}
                        onChange={(event) => {
                          save.mutate(
                            { type, channel, isEnabled: event.target.checked },
                            { onError },
                          );
                        }}
                        className="size-4 shrink-0 accent-honey-500"
                      />
                      <span className="text-label text-ink-900">{label}</span>
                    </label>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/*
        The blocked capability, explained rather than hidden. The prototype's
        "Push notifications are blocked in your browser settings. To enable
        them, update your browser's notification permissions for this site." is
        the pattern — state the cause and the fix.
      */}
      <p className="mt-4 rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2.5 text-meta text-ink-700">
        Alerts straight to your phone are still to come. Anything urgent, we ring you.
      </p>
    </section>
  );
}

function Detail({
  label,
  absent,
  icon: Icon,
  children,
}: {
  label: string;
  absent?: string;
  icon?: typeof Mail;
  children: string | null | undefined;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-meta text-ink-500">
        {Icon ? <Icon aria-hidden="true" className="size-3.5" /> : null}
        {label}
      </dt>
      <dd className="mt-0.5 text-body text-ink-900">
        {children ?? <span className="italic text-ink-500">{absent ?? 'Not on file'}</span>}
      </dd>
    </div>
  );
}
